import DropdownIcon from "@mui/icons-material/ArrowDropDown";
import DropupIcon from "@mui/icons-material/ArrowDropUp";
import EditIcon from "@mui/icons-material/Edit";
import InventoryIcon from "@mui/icons-material/Inventory";
import RestoreIcon from "@mui/icons-material/Restore";
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Stack,
  Grid,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { putAPICall } from "lib/callApi";
import {
  DELETE_KPI,
  // MERGE_KPI
  RESTORE_KPI,
} from "lib/constants";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { companyKpisActions } from "../../../redux/slice/companyKpis";
import "../../../styles/kpis.scss";
import EditKpiModal from "./EditKpiModal";

function KpiEditTable() {
  const theme = useTheme();
  const isMobileDisplay = useMediaQuery(theme.breakpoints.only("xs"));
  const companyKpis = useSelector((state) => state.companyKpis.companyKpis.list || []);
  const totalKpiCount = useSelector((state) => state.companyKpis.companyKpis.count || 0);
  const dispatch = useDispatch();
  const [editModalData, setEditModalData] = useState({ isModalOpen: false, kpiId: null, name: "" });
  const [selected, setSelected] = useState([]);
  const [disableArchiveButton, setDisableArchiveButton] = useState(true);
  const [disableRestoreButton, setDisableRestoreButton] = useState(true);
  const [kpiPayload, setKpiPayload] = useState({
    search: "",
    pageSize: 25,
    pageNumber: 0,
    filter: {
      includeActivities: true,
      onlyCompanyKpis: true,
      showActiveKpis: true,
      showInActiveKpis: true,
      showArchivedKpis: false,
    },
    sort: null,
  });
  const [sortedBy, setSortedBy] = useState("kpiNameDown");
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    dispatch(companyKpisActions.getCompanyKpisRequest({ ...kpiPayload }));
  }, [kpiPayload]);

  useEffect(() => {
    if (sortedBy) {
      setKpiPayload((prevPayload) => ({
        ...prevPayload,
        sort: sortedBy,
      }));
    }
  }, [sortedBy]);

  const handlePageChange = (_, newPage) => {
    setKpiPayload((prevPayload) => ({ ...prevPayload, pageNumber: newPage }));
  };

  const handleChangeRowsPerPage = (event) => {
    setKpiPayload((prevPayload) => ({
      ...prevPayload,
      pageSize: event.target.value || 25,
    }));
  };

  const handleDeleteKpiAPI = async (kpiIds) => {
    const response = await putAPICall(DELETE_KPI, true, kpiIds);
    return response;
  };

  const handleRestoreKpiAPI = async (kpiIds) => {
    const response = await putAPICall(RESTORE_KPI, true, kpiIds);
    return response;
  };

  // const handleMergeKpiAPI = async (payload) => {
  //   const response = await putAPICall(MERGE_KPI, true, payload);
  //   return response;
  // };

  const handleDeleteKpiClick = (deleteKpi) => {
    Swal.fire({
      title: `Are you sure you want to archive this KPI ${deleteKpi?.name}?`,
      text: deleteKpi?.KpiTemplates?.length
        ? `This will be removed from KPI templates: ${deleteKpi?.KpiTemplates?.map(
            (kpiTemplate) => kpiTemplate?.name
          )?.join(", ")}`
        : "",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, archive it!",
      reverseButtons: true,
    })
      .then(async (result) => {
        if (result.isConfirmed) {
          const response = await handleDeleteKpiAPI([deleteKpi?.id]);

          if (!response?.data?.success) {
            enqueueSnackbar(
              JSON.stringify({
                icon: "error",
                title: "error",
                text: "Could not able to archive kpi activity, Please try again!",
              }),
              {
                variant: "custom",
              }
            );
            return;
          }

          enqueueSnackbar(
            JSON.stringify({
              icon: "success",
              title: "Success",
              text: "Kpi activity archived successfully!",
            }),
            {
              variant: "custom",
            }
          );

          dispatch(companyKpisActions.getCompanyKpisRequest({ ...kpiPayload }));
        }
      })
      .catch();
  };

  const handleDeleteSelectedClicked = async () => {
    if (selected?.length < 2) return;
    Swal.fire({
      title: `Are you sure you want to archive selected KPI's?`,
      html: selected?.some((kpi) => kpi?.KpiTemplates?.length > 0)
        ? `This will be removed from KPI templates: 
        ${selected
          .map((kpi) =>
            kpi?.KpiTemplates?.length
              ? `<br><br> ${kpi?.name}: ${
                  kpi?.KpiTemplates?.length > 1
                    ? kpi.KpiTemplates.map((kpiTemplate) => kpiTemplate?.name)?.join(", ")
                    : kpi?.KpiTemplates?.[0]?.name
                }`
              : null
          )
          .filter((kpi) => kpi)}`
        : "",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, archive it!",
      reverseButtons: true,
    })
      .then(async (result) => {
        if (result.isConfirmed) {
          const response = await handleDeleteKpiAPI(
            selected
              ?.map((kpiObj) => (!kpiObj?.deletedAt ? kpiObj?.id : null))
              .filter((kpiId) => kpiId)
          );
          if (!response?.data?.success) {
            enqueueSnackbar(
              JSON.stringify({
                icon: "error",
                title: "error",
                text: "Could not archive kpi activities, Please try again!",
              }),
              {
                variant: "custom",
              }
            );
            return;
          }

          enqueueSnackbar(
            JSON.stringify({
              icon: "success",
              title: "Success",
              text: "Kpi activities archived successfully!",
            }),
            {
              variant: "custom",
            }
          );
          dispatch(companyKpisActions.getCompanyKpisRequest({ ...kpiPayload }));
        }
      })
      .catch();
  };

  const handleRestoreSelectedClicked = async (passedId) => {
    if (selected?.length < 2 && !passedId) return;
    Swal.fire({
      title: `Are you sure you want to restore selected KPI${passedId ? "" : `'s`}'s?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: `Restore${passedId ? "" : " Selected"}`,
      reverseButtons: true,
    })
      .then(async (result) => {
        if (result.isConfirmed) {
          const filteredByDeleted = passedId
            ? [passedId]
            : selected
                ?.map((kpiObj) => (kpiObj?.deletedAt ? kpiObj?.id : null))
                .filter((kpiId) => kpiId);
          const response = await handleRestoreKpiAPI(filteredByDeleted);
          if (!response?.data?.success) {
            enqueueSnackbar(
              JSON.stringify({
                icon: "error",
                title: "error",
                text: `Could not restore kpi activit${passedId ? "y" : "ies"}, Please try again!`,
              }),
              {
                variant: "custom",
              }
            );
            return;
          }
          enqueueSnackbar(
            JSON.stringify({
              icon: "success",
              title: "Success",
              text: `Kpi activit${passedId ? "y" : "ies"} restored successfully!`,
            }),
            {
              variant: "custom",
            }
          );
          setSelected([]);
          dispatch(companyKpisActions.getCompanyKpisRequest({ ...kpiPayload }));
        }
      })
      .catch();
  };

  // TODO: Need to uncomment merge kpi flow once all requirements are cleared
  // function generateSections() {
  //   return selected.map(
  //     (section) => `
  //     <div class="section">
  //       <div style="display: flex; justify-content: space-between; align-items: center;">
  //         <p class="label">${section.name}</p>
  //         <button class="customButton" style="margin: 5px 0px; padding: 10px; border: none; cursor: pointer;">USE</button>
  //       </div>
  //     </div>
  //   `
  //   );
  // }

  // const handleMergeSelectedClicked = async () => {
  //   if (selected?.length < 2) return;

  //   Swal.fire({
  //     title: "Are you sure you want to merge multiple KPIs into one KPI?",
  //     html: `<div id="sectionsContainer">
  //               ${generateSections().join("")}
  //             </div>
  //             <input class="swal2-input" id="name" type="text" placeholder="New KPI name" required />`,
  //     icon: "warning",
  //     showCancelButton: true,
  //     confirmButtonColor: "#3085d6",
  //     cancelButtonColor: "#d33",
  //     confirmButtonText: "Save",
  //     reverseButtons: true,
  //     preConfirm: () => {
  //       const inputField = document.getElementById("name");
  //       return inputField.value;
  //     },
  //     didOpen: () => {
  //       const sectionsContainer = document.getElementById("sectionsContainer");
  //       const inputField = document.getElementById("name");
  //       const saveButton = Swal.getConfirmButton();
  //       saveButton.disabled = true;

  //       sectionsContainer.addEventListener("click", (event) => {
  //         if (event.target && event.target.classList.contains("customButton")) {
  //           const label = event.target.closest(".section").querySelector(".label").textContent;
  //           if (inputField) {
  //             inputField.value = label;
  //             saveButton.disabled = inputField.value === "";
  //           }
  //         }
  //       });

  //       // Update the Save button state on input change
  //       inputField.addEventListener("input", () => {
  //         saveButton.disabled = inputField.value === "";
  //       });
  //     },
  //   })
  //     .then(async (result) => {
  //       if (result.isConfirmed) {
  //         const response = await handleMergeKpiAPI({
  //           kpiIds: selected?.map((kpiObj) => kpiObj?.id),
  //           name: result?.value,
  //         });
  //         if (!response?.data?.success) {
  //           Swal.fire({
  //             icon: "error",
  //             title: "error",
  //             confirmButtonColor: "#267FEA",
  //             text: "Could not able to merge kpi activities, Please try again!",
  //           }).then();
  //           return;
  //         }

  //         Swal.fire({
  //           icon: "success",
  //           title: "Success",
  //           confirmButtonColor: "#267FEA",
  //           text: "Kpi activities merged successfully!",
  //         }).then();
  //         dispatch(companyKpisActions.getCompanyKpisRequest({ ...kpiPayload }));
  //       }
  //     })
  //     .catch();
  // };

  const isSelected = (row) => selected.find((user) => user.id === row.id);

  const handleClick = (event, row) => {
    const selectedIndex = selected.findIndex((user) => user.id === row.id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, row);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);

    let activeKpiCount = 0;
    let archivedCount = 0;
    // eslint-disable-next-line array-callback-return, consistent-return
    newSelected.forEach((record) => {
      if (record?.deletedAt) {
        archivedCount += 1;
      }
      if (!record?.deletedAt) {
        activeKpiCount += 1;
      }
    });

    setDisableArchiveButton(activeKpiCount < 2);
    setDisableRestoreButton(archivedCount < 2);
  };

  const handleActiveInActiveFilter = (event) => {
    const newKpiPayload = { ...kpiPayload };
    newKpiPayload.pageNumber = 0;
    newKpiPayload.filter[event.target.value] = event.target.checked;
    setKpiPayload(newKpiPayload);
  };

  return (
    <>
      <MDBox>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            "& .MuiInputLabel-formControl": { lineHeight: "15px" },
          }}
        >
          <Grid container>
            <Grid item xs={12} sm={2}>
              <MDBox my={1}>
                <MDInput
                  label="Search"
                  value={kpiPayload.search}
                  onChange={(e) =>
                    setKpiPayload({
                      ...kpiPayload,
                      pageNumber: 0,
                      search: e.target.value,
                    })
                  }
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} sm={10}>
              <MDBox ml={2} my={1}>
                <FormGroup
                  aria-label="position"
                  sx={{ flexDirection: isMobileDisplay ? "column" : "row" }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        value="showActiveKpis"
                        checked={kpiPayload.filter.showActiveKpis}
                        onChange={handleActiveInActiveFilter}
                      />
                    }
                    label="Show Active"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        value="showInActiveKpis"
                        checked={kpiPayload.filter.showInActiveKpis}
                        onChange={handleActiveInActiveFilter}
                      />
                    }
                    label="Show Inactive"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        value="showArchivedKpis"
                        checked={kpiPayload.filter.showArchivedKpis}
                        onChange={handleActiveInActiveFilter}
                      />
                    }
                    label="Show Archived"
                  />
                </FormGroup>
              </MDBox>
            </Grid>
            <Grid item xs={12}>
              <Stack sx={{ flexDirection: "row" }} my={1}>
                <MDButton
                  variant="gradient"
                  type="submit"
                  color="newColor"
                  disabled={disableRestoreButton}
                  onClick={() => handleRestoreSelectedClicked()}
                >
                  RESTORE
                </MDButton>
                <MDButton
                  variant="gradient"
                  type="submit"
                  color="newColor"
                  sx={{ marginLeft: 2 }}
                  disabled={disableArchiveButton}
                  onClick={handleDeleteSelectedClicked}
                >
                  ARCHIVE SELECTED
                </MDButton>
                {/* <MDButton
              variant="gradient"
              type="submit"
              color="newColor"
              sx={{ marginLeft: 2 }}
              disabled={selected?.length < 2}
              onClick={handleMergeSelectedClicked}
            >
              MERGE SELECTED
            </MDButton> */}
                <MDButton
                  type="submit"
                  color="primaryRed"
                  sx={{ marginLeft: 2 }}
                  onClick={() =>
                    setEditModalData({
                      kpiId: null,
                      isModalOpen: true,
                      name: null,
                    })
                  }
                >
                  + Add new KPI
                </MDButton>
              </Stack>
            </Grid>
          </Grid>
        </Box>
        <MDBox mt={2}>
          <TableContainer component={Paper} className="edit-kpi-table">
            <Table aria-label="simple table">
              <TableHead sx={{ display: "table-header-group" }}>
                <TableRow>
                  <TableCell />
                  <TableCell
                    onClick={() =>
                      setSortedBy((prev) => {
                        if (prev === "kpiNameUp" || !prev) {
                          return "kpiNameDown";
                        }
                        return "kpiNameUp";
                      })
                    }
                  >
                    Kpi Name
                    {sortedBy === "kpiNameUp" && <DropupIcon />}
                    {sortedBy === "kpiNameDown" && <DropdownIcon />}
                  </TableCell>
                  <TableCell
                    onClick={() =>
                      setSortedBy((prev) => {
                        if (prev === "templatesAssignedUp" || !prev) {
                          return "templatesAssignedDown";
                        }
                        return "templatesAssignedUp";
                      })
                    }
                  >
                    Templates Assigned
                    {sortedBy === "templatesAssignedUp" && <DropupIcon />}
                    {sortedBy === "templatesAssignedDown" && <DropdownIcon />}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {[...companyKpis]?.map((row, index) => {
                  const isItemSelected = !!isSelected(row);
                  const labelId = `manage-kpi-checkbox-${index}`;

                  return (
                    <TableRow
                      key={row.id}
                      sx={[
                        {
                          cursor: "pointer",
                        },
                        !(index % 2) && {
                          cursor: "pointer",
                          background: "#f5f5f5",
                        },
                        isItemSelected && {
                          cursor: "pointer",
                          background: theme.palette.gray[80],
                        },
                      ]}
                      className={isItemSelected ? "selected" : null}
                      onClick={(event) => handleClick(event, row)}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={isItemSelected}
                          inputProps={{
                            "aria-labelledby": labelId,
                          }}
                        />
                      </TableCell>
                      <TableCell>{row?.name}</TableCell>
                      <TableCell>
                        {row?.deletedAt
                          ? null
                          : row?.KpiTemplates?.map((kpiTemplate) => kpiTemplate.name)?.join(", ")}
                      </TableCell>
                      <TableCell sx={{ pr: "1rem !important" }}>
                        {row?.deletedAt ? (
                          <MDBox display="flex" justifyContent="center" alignItems="center">
                            <RestoreIcon
                              style={{ cursor: "pointer" }}
                              onClick={(e) => {
                                e?.stopPropagation();
                                handleRestoreSelectedClicked(row.id);
                              }}
                            />
                          </MDBox>
                        ) : (
                          <MDBox display="flex" justifyContent="center" alignItems="center">
                            <EditIcon
                              style={{ cursor: "pointer" }}
                              onClick={(e) => {
                                e?.stopPropagation();
                                setEditModalData({
                                  kpiId: row.id,
                                  isModalOpen: true,
                                  name: row.name,
                                });
                              }}
                            />
                            <InventoryIcon
                              style={{ cursor: "pointer", marginLeft: "5px" }}
                              onClick={(e) => {
                                e?.stopPropagation();
                                handleDeleteKpiClick(row);
                              }}
                            />
                          </MDBox>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 20, 25, 50]}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleChangeRowsPerPage}
              component="div"
              count={totalKpiCount}
              rowsPerPage={kpiPayload?.pageSize}
              page={kpiPayload.pageNumber}
              sx={{ flex: "1 1 auto" }}
            />
          </TableContainer>
        </MDBox>
      </MDBox>
      {editModalData.isModalOpen && (
        <EditKpiModal
          editModalData={editModalData}
          open={editModalData.isModalOpen}
          handleClose={() => {
            setEditModalData({ kpiId: null, isModalOpen: false, name: "" });
            // trigger the dispatch useEffect
            setKpiPayload((prev) => ({ ...prev }));
          }}
        />
      )}
    </>
  );
}

export default KpiEditTable;
